import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { selectedCompanyStorage } from '../../storage/company/index';
import { useAppMutation } from '../use-app-mutation.hook';
import { useAppQuery } from '../use-app-query.hook';
import type {
  FinalizeVerificationRequest,
  FinalizeVerificationResponse,
} from './billing-payment.types';
import { parseFinalizeVerificationResponse } from './parse-finalize-verification-response.util';

export const useSetupPaymentMethod = ({
  enabled = true,
  companyId,
}: {
  enabled?: boolean;
  companyId?: string;
}) => {
  const defaultCompanyId = selectedCompanyStorage(({ companyId }) => companyId);

  if (!companyId) {
    companyId = defaultCompanyId;
  }

  const { mutateAsync: finalizeVerificationAsync, isPending } = useAppMutation<
    FinalizeVerificationResponse,
    unknown,
    FinalizeVerificationRequest
  >([QueryKey.REFOUND_VERIFICATION_INTENT], {
    mutationFn: async ({
      paymentIntentId,
      companyId,
      stripeSubscriptionId,
      deferSubscriptionPayment,
    }) => {
      const raw = await ApiService.post<
        unknown,
        Pick<
          FinalizeVerificationRequest,
          'companyId' | 'deferSubscriptionPayment' | 'stripeSubscriptionId'
        >
      >(
        { endpoint: BackendRoute.BILLING, routePath: [paymentIntentId] },
        { body: { companyId, stripeSubscriptionId, deferSubscriptionPayment } },
      );

      return parseFinalizeVerificationResponse(raw);
    },
  });

  const {
    data: { clientSecret = null } = {},
    isPending: isLoadingVerification,
    refetch,
  } = useAppQuery<{
    clientSecret: null | string;
  }>({
    queryKey: [QueryKey.CREATE_VERIFICATION_INTENT, companyId],
    queryFn: () => ApiService.post({ endpoint: BackendRoute.BILLING }, { body: {} }),
    enabled: enabled,
    refetchInterval: false,
    refetchOnMount: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  return {
    verificationClientSecret: clientSecret,
    getFreshVerificationClientSecret: refetch,
    finalizeVerificationAsync,
    isPendingRefound: isPending,
    isLoadingVerification,
  };
};
